import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { SEO } from '@/components/SEO';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Mail, MessageCircle, Phone, Clock, Send } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import logo from '@/assets/logo.png';

export default function Support() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast({
      title: "Message Sent!",
      description: "We'll get back to you within 24 hours.",
    });
    
    setFormData({ name: '', email: '', subject: '', message: '' });
    setSubmitting(false);
  };

  const contactMethods = [
    {
      icon: Mail,
      title: "Email Support",
      description: "support@sentinel.com",
      info: "Response within 24 hours",
      color: "text-primary"
    },
    {
      icon: MessageCircle,
      title: "Live Chat",
      description: "Chat with our team",
      info: "Available 9 AM - 6 PM IST",
      color: "text-primary"
    },
    {
      icon: Phone,
      title: "Phone Support",
      description: "+91 1800-123-4567",
      info: "Monday - Friday, 9 AM - 6 PM IST",
      color: "text-primary"
    }
  ];

  const faqs = [
    {
      question: "How do I reset my password?",
      answer: "Go to the login page and click 'Forgot Password'. You'll receive a reset link via email."
    },
    {
      question: "Can I import historical data?",
      answer: "Yes! You can import transactions and invoices from the past 7 years. Go to Sync → Import Historical Data."
    },
    {
      question: "How do I upgrade my plan?",
      answer: "Navigate to Settings → Billing → Upgrade Plan. Changes take effect immediately."
    },
    {
      question: "Is my data encrypted?",
      answer: "Yes, all data is encrypted with AES-256 at rest and TLS 1.3 in transit. We follow SOC 2 standards."
    }
  ];

  return (
    <>
      <SEO
        title="Support - Get Help | Sentinel"
        description="Need help with Sentinel? Contact our support team via email, chat, or phone. Browse FAQs and resources."
      />
      
      <div className="min-h-screen bg-background">
        <nav className="border-b border-border/50 bg-background/95 backdrop-blur-xl sticky top-0 z-50">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
                <img src={logo} alt="SENTINEL Logo" className="w-10 h-10 brightness-0 dark:brightness-100 dark:invert" />
                <span className="text-2xl font-bold">SENTINEL</span>
              </div>
              <div className="flex items-center gap-4">
                <ThemeToggle />
                <Button onClick={() => navigate('/auth')}>Sign In</Button>
              </div>
            </div>
          </div>
        </nav>

        <div className="container mx-auto px-6 py-12 max-w-7xl">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold mb-4 pb-2">How Can We Help?</h1>
            <p className="text-xl text-muted-foreground">
              Get in touch with our support team
            </p>
          </div>

          {/* Contact Methods */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {contactMethods.map((method, index) => (
              <Card key={index} className="p-8 text-center hover:shadow-card-hover transition-all border-border/50">
                <div className={`w-14 h-14 rounded-xl bg-gradient-primary flex items-center justify-center mx-auto mb-4 shadow-glow`}>
                  <method.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">{method.title}</h3>
                <p className="font-medium text-primary mb-2">{method.description}</p>
                <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
                  <Clock size={14} />
                  {method.info}
                </p>
              </Card>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="p-8 border-border/50">
              <h2 className="text-2xl font-bold mb-6 pb-1">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Your full name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="your@email.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                    placeholder="How can we help?"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    placeholder="Describe your issue or question..."
                    rows={6}
                  />
                </div>

                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? (
                    "Sending..."
                  ) : (
                    <>
                      <Send className="mr-2" size={18} />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </Card>

            {/* FAQs */}
            <div>
              <h2 className="text-2xl font-bold mb-6 pb-1">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <Card key={index} className="p-6 border-border/50">
                    <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </Card>
                ))}
              </div>

              <Card className="p-6 mt-6 bg-surface border-primary/20">
                <h3 className="font-semibold mb-2">Can't find what you're looking for?</h3>
                <p className="text-muted-foreground mb-4">
                  Check out our comprehensive documentation
                </p>
                <Button variant="outline" onClick={() => navigate('/documentation')} className="w-full">
                  View Documentation
                </Button>
              </Card>
            </div>
          </div>

          {/* Enterprise Support */}
          <Card className="p-12 text-center mt-16 bg-gradient-primary border-0">
            <h2 className="text-4xl font-bold mb-4 pb-1 text-white">Need Enterprise Support?</h2>
            <p className="text-xl text-white/90 mb-8">
              Get dedicated support, SLA guarantees, and priority assistance
            </p>
            <Button size="lg" variant="secondary" onClick={() => navigate('/pricing')}>
              Explore Enterprise Plan
            </Button>
          </Card>
        </div>

        <footer className="border-t border-border/50 py-8 bg-surface/30 mt-20">
          <div className="container mx-auto px-6 text-center text-sm text-muted-foreground">
            <p>&copy; 2025 SENTINEL. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
}
